import { usePendingConversationMessages } from '../message-box/whatsapp/use-pending-conversation-messages';
import { reconcileRecentTimelineEvents } from 'src/modules/shared/communications/utils/cache/timeline-items/recent-events.util';
import { awaitCancelableRequest } from 'src/modules/shared/utils/api/cancelable-request.util';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  type TCTimelineItemSearchResponse as TimelineItemSearchResponse,
  type TCTimelineItemSearchBodyParams as TimelineItemSearchBodyParams,
  CrmTimelineItemsService,
  type TCSearcherTimelineItemDocument as TimelineItemDocument,
} from 'src/api/generated';
import { mergeTimelineItems } from '../../utils/timeline/merge-items.util';
import { SEC_IN_MS } from 'src/utils/date';

interface UseTimelineItemsParams extends TimelineItemSearchBodyParams {}

interface UseTimelineItemsResponse extends ApiResponse {
  timelineItems: TimelineItemDocument[];
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useSearchTimelineItems = (
  params: UseTimelineItemsParams,
  enabled = true
): UseTimelineItemsResponse => {
  const queryClient = useQueryClient();
  const fetchTimelineItems = async ({
    pageParam,
    signal,
  }: {
    pageParam: string | undefined;
    signal: AbortSignal;
  }): Promise<TimelineItemSearchResponse> => {
    const response = await awaitCancelableRequest(
      CrmTimelineItemsService.searchTimelineItems({
        ...params,
        pageToken: pageParam,
      }),
      signal
    );
    signal.throwIfAborted();
    return {
      ...response,
      items: reconcileRecentTimelineEvents(
        queryClient,
        ['crm/timeline-items/search', params],
        response.items,
        pageParam === undefined
      ),
    };
  };

  const getNextPageParam = (lastPage: TimelineItemSearchResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage: fetchNextTimelineItemsPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/timeline-items/search', params],
    queryFn: fetchTimelineItems,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
    refetchInterval: SEC_IN_MS * 30,
  });

  const pendingItems = usePendingConversationMessages(
    ['crm/timeline-items/search', params],
    data?.pages.flatMap((page) => page.items) ?? []
  );
  const timelineItems = useMemoizedArrayData<TimelineItemDocument>(
    mergeTimelineItems(pendingItems)
  );

  return {
    loading,
    error,
    timelineItems,
    fetchNextPage: async () => {
      await fetchNextTimelineItemsPage();
    },
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};
